import type { MentionMap } from '@atlassian/ai-model-io/utils/mention-map';

import type { IntentSchemaId } from '../../../types/prompt-request-types';
import type {
	UpdateIdMap,
	EditorPluginAIConfigItem,
} from '../../prebuilt/config-items/config-items';
import { sliceOrNodeToMarkdown } from '../../prebuilt/config-items/utils/slice-or-node-to-markdown';
import { convoAIApiUrl } from '../../prebuilt/utils';

import { getSupportedMarkdowns } from './get-supported-markdown-from-schema';
import type { AIExperienceMachineContextForRequest, RequestOptions } from './types';

type Options = {
	context: AIExperienceMachineContextForRequest;
	configItem: EditorPluginAIConfigItem;
	mentionMap?: MentionMap;
	intentSchemaId: IntentSchemaId;
	updateIdMap: UpdateIdMap;
};

const buildContentAri = ({
	product,
	objectId,
	cloudId,
}: {
	product: string;
	objectId?: string | number;
	cloudId?: string;
}): string => {
	return `ari:cloud:${product.toLowerCase()}:${cloudId}:content/${objectId}`;
};

/**
 * A function that will make the payload for the Convo AI API.
 *
 * @see https://convo-ai.us-east-1.staging.atl-paas.net/api/swagger-ui/index.html#/ai-features-controller/editorStream
 */
export const getRequestOptions4ConvoAI = ({
	context,
	configItem,
	mentionMap,
	intentSchemaId,
	updateIdMap,
}: Options): RequestOptions => {
	const { aiProvider, userInput, editorView, positions, intl, latestPromptResponse } = context;
	const editorSchema = editorView.state.schema;
	const { product, objectId, getChannelVariables } = aiProvider;

	if (objectId === undefined) {
		return { error: 'Undefined objectId' };
	}

	const cloudId = getChannelVariables?.('rovo-agent')?.cloudId;
	if (cloudId === undefined) {
		return { error: 'Undefined cloudId' };
	}

	const featureId = configItem.featureId;
	if (!featureId) {
		return { error: `Unknown feature id: ${intentSchemaId}` };
	}

	const { experienceId, endpoint } = configItem;
	const aiSessionId = context.analyticsContext?.aiSessionId;
	const idMap = {};

	// if there is no selection, we want to pass only the doc
	// if there is selection, we want to pass only the selection (if we pass doc and selection, the doc will be ignored by BE)
	const needDoc = context.isEmpty;
	const needSelection = !context.isEmpty;
	const doc = needDoc
		? sliceOrNodeToMarkdown({
				node: editorView.state.doc,
				editorView,
				convertTo: 'markdown-plus',
				existingIdMap: idMap,
				updateIdMap,
				mentionMap,
				aiSessionId,
			})
		: undefined;
	const docContent = doc?.markdown;
	const docIdMap = doc?.idMap;
	const docContentStatistics = doc?.contentStatistics;

	const selection = needSelection
		? sliceOrNodeToMarkdown({
				slice: editorView.state.doc.slice(positions[0], positions[1]),
				editorView,
				convertTo: 'markdown-plus',
				existingIdMap: docIdMap,
				updateIdMap,
				mentionMap,
				aiSessionId,
			})
		: undefined;

	return {
		doc: docContent,
		selection: selection?.markdown,
		contentStatistics: docContentStatistics || selection?.contentStatistics,
		isConvoAI: true,
		experienceId: experienceId || 'editor',
		endpoint: endpoint || convoAIApiUrl,
		payload: JSON.stringify({
			ai_feature_input: {
				feature_args: {
					// NOTE: we want to ensure we use undefined instead of null. This is because the JSON Serializer will remove props
					// that are undefined, but will not remove props that are null.
					user_input: userInput || undefined,
					selected_context: latestPromptResponse || selection?.markdown || undefined,
					document_context: docContent || undefined,
					supported_markdown_nodes: getSupportedMarkdowns(editorSchema),
					output_mime_type: 'text/markdown',
				},
				feature: featureId,
				/**
				 * We can still get a response from the server if the contentAri is not valid
				 * but we will get 500, internal server error if the contentAri is undefined.
				 * It is not required for a prompt like improve writing so we can ignore it.
				 * But we should still log an analytics event to track when this occurs.
				 */
				content_ari: buildContentAri({ product, objectId, cloudId }),
				ai_session_id: aiSessionId,
				locale: intl.locale,
			},
		}),
	};
};
